import { makeAutoObservable, runInAction } from "mobx";
import { useCloud } from "./useCloud";

class ReportDownloadStore {
  // Store report requests by type
  reportRequests = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  // Add a new report request
  addRequest({ reportId, reportType, dateType, startDate, endDate, label }) {
    // If a request for this type already exists and is pending, don't add new one
    const existingRequest = this.reportRequests.get(reportType);
    if (existingRequest?.status === "pending") {
      return existingRequest;
    }

    const newRequest = {
      reportId,
      reportType,
      dateType,
      startDate,
      endDate,
      label,
      status: "pending",
      requestedAt: new Date(),
      downloadUrl: null,
      error: null,
    };

    runInAction(() => {
      this.reportRequests.set(reportType, newRequest);
    });

    return newRequest;
  }

  // Update request status
  updateRequestStatus(reportType, status, data = {}) {
    const request = this.reportRequests.get(reportType);
    if (!request) return;

    runInAction(() => {
      this.reportRequests.set(reportType, {
        ...request,
        status,
        ...data,
      });
    });
  }

  // Mark request as completed
  markAsCompleted(reportType, downloadUrl) {
    this.updateRequestStatus(reportType, "completed", { downloadUrl });
  }

  // Mark request as failed
  markAsFailed(reportType, error) {
    this.updateRequestStatus(reportType, "failed", { error });
  }

  // Get request by type
  getRequest(reportType) {
    return this.reportRequests.get(reportType);
  }

  // Check if a report type is currently being processed
  isProcessing(reportType) {
    const request = this.reportRequests.get(reportType);
    return request?.status === "pending";
  }

  // Clear completed or failed requests
  clearRequest(reportType) {
    runInAction(() => {
      this.reportRequests.delete(reportType);
    });
  }

  // Get all requests
  getAllRequests() {
    return Array.from(this.reportRequests.values());
  }
}

// Create a single instance of the store
const reportDownloadStore = new ReportDownloadStore();

// Hook for using the store
export const useReportDownload = () => {
  const { createDownloadRequest, downloadReport } = useCloud();

  const initiateDownload = async ({
    reportType,
    dateType,
    startDate,
    endDate,
    label,
  }) => {
    try {
      // Check if already processing
      if (reportDownloadStore.isProcessing(reportType)) {
        return {
          success: false,
          message: "A download for this report type is already in progress",
        };
      }

      const response = await createDownloadRequest({
        reportType,
        dateType,
        startDate,
        endDate,
      });

      if (response?.status === "success" && response?.data?.reportId) {
        // Add to store
        reportDownloadStore.addRequest({
          reportId: response.data.reportId,
          reportType,
          dateType,
          startDate,
          endDate,
          label,
        });

        return {
          success: true,
          reportId: response.data.reportId,
        };
      }

      return {
        success: false,
        message: "Failed to initiate download",
      };
    } catch (error) {
      reportDownloadStore.markAsFailed(reportType, error.message);
      return {
        success: false,
        message: error.message,
      };
    }
  };

  const checkDownloadStatus = async (reportType) => {
    const request = reportDownloadStore.getRequest(reportType);
    if (!request || request.status !== "pending") return;

    try {
      const response = await downloadReport(request.reportId);

      if (response?.status === "success") {
        const _requestUpdate = response.data.find(
          (_request) => _request.reportId == request.reportId
        );
        if (_requestUpdate.status === "completed" && _requestUpdate.filePath) {
          reportDownloadStore.markAsCompleted(
            reportType,
            _requestUpdate.filePath
          );
          return {
            success: true,
            status: "completed",
            downloadUrl: _requestUpdate.filePath,
          };
        } else if (_requestUpdate.status === "failed") {
          reportDownloadStore.markAsFailed(
            reportType,
            "Report generation failed"
          );
          return {
            success: false,
            status: "failed",
            message: "Report generation failed",
          };
        }
      }

      return {
        success: true,
        status: "pending",
      };
    } catch (error) {
      reportDownloadStore.markAsFailed(reportType, error.message);
      return {
        success: false,
        status: "failed",
        message: error.message,
      };
    }
  };

  return {
    initiateDownload,
    checkDownloadStatus,
    getRequest: reportDownloadStore.getRequest.bind(reportDownloadStore),
    getAllRequests:
      reportDownloadStore.getAllRequests.bind(reportDownloadStore),
    clearRequest: reportDownloadStore.clearRequest.bind(reportDownloadStore),
    isProcessing: reportDownloadStore.isProcessing.bind(reportDownloadStore),
  };
};

export default reportDownloadStore;
